import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, ArrowRight, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OFFICE_IMG = "https://images.pexels.com/photos/8082328/pexels-photo-8082328.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Contact() {
  const [tab, setTab] = useState("message");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", subject: "Probate Consultation", relationship: "", best_time: "Morning (8am–12pm)", message: "" });
  const [appt, setAppt] = useState({ name: "", email: "", phone: "", preferred_date: "", preferred_time: "10:00 AM", notes: "" });
  const [busy, setBusy] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, contact);
      toast.success("Message received", { description: "We'll get back within 24 hours." });
      setContact({ name: "", email: "", phone: "", subject: "Probate Consultation", relationship: "", best_time: "Morning (8am–12pm)", message: "" });
    } catch {
      toast.error("Failed to send message");
    } finally {
      setBusy(false);
    }
  };

  const submitAppt = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/appointments`, appt);
      toast.success("Appointment requested", { description: `We'll confirm ${appt.preferred_date} at ${appt.preferred_time}.` });
      setAppt({ name: "", email: "", phone: "", preferred_date: "", preferred_time: "10:00 AM", notes: "" });
    } catch {
      toast.error("Failed to book appointment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-navy-50/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="section-overline">Let's Talk</span>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight tracking-tight">
            Visit us, call us, or <em className="italic text-gold-600">book a quiet seat</em>.
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Left: info */}
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-3xl overflow-hidden bg-navy-900 relative">
              <img src={OFFICE_IMG} alt="Office" className="h-56 w-full object-cover opacity-80" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold-400 font-semibold">Visit Us</div>
                <div className="mt-1 font-heading text-2xl">Probate Solution OC</div>
              </div>
            </div>

            <div className="glass rounded-3xl p-7 space-y-5" data-testid="contact-info">
              <InfoRow icon={MapPin} label="Office" value={<>2134 Main St. Suite 140<br />Huntington Beach, CA 92648<br />United States</>} />
              <InfoRow icon={Phone} label="Phone" value={<a href="tel:+17143913645" className="hover:text-gold-600">(714) 391-3645</a>} />
              <InfoRow icon={Mail} label="Email" value="marynguyenrealtor@gmail.com" />
              <InfoRow icon={Clock} label="Hours" value={<>9:00am – 6:00pm<br />By appointment only</>} />
            </div>

            {/* Map placeholder */}
            <div className="relative rounded-3xl overflow-hidden border border-navy-100 h-56 bg-white" data-testid="map-placeholder">
              <iframe
                title="Map"
                src="https://www.google.com/maps?q=Huntington+Beach,+CA&output=embed"
                className="h-full w-full grayscale-[40%]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute top-4 left-4 glass rounded-full px-4 py-1.5 text-xs font-semibold text-navy-800 flex items-center gap-1.5">
                <MapPin size={12} className="text-gold-600" />
                Huntington Beach, CA
              </div>
            </div>
          </div>

          {/* Right: form / appointment */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass rounded-3xl p-8 sm:p-10"
            >
              <div className="flex gap-2 p-1 bg-navy-100/60 rounded-full w-fit mb-6" data-testid="contact-tabs">
                <button
                  onClick={() => setTab("message")}
                  data-testid="tab-message"
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    tab === "message" ? "bg-white text-navy-900 shadow-sm" : "text-navy-600 hover:text-navy-900"
                  }`}
                >
                  Send a message
                </button>
                <button
                  onClick={() => setTab("appointment")}
                  data-testid="tab-appointment"
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    tab === "appointment" ? "bg-white text-navy-900 shadow-sm" : "text-navy-600 hover:text-navy-900"
                  }`}
                >
                  Book appointment
                </button>
              </div>

              {tab === "message" ? (
                <form onSubmit={submitContact} className="space-y-4" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input label="Name" required value={contact.name} onChange={(v) => setContact({ ...contact, name: v })} testid="contact-name" />
                    <Input label="Email" type="email" required value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} testid="contact-email" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input label="Phone" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} testid="contact-phone" />
                    <Input label="Subject" value={contact.subject} onChange={(v) => setContact({ ...contact, subject: v })} testid="contact-subject" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-navy-700">Your relationship with decedent</label>
                      <select
                        value={contact.relationship}
                        onChange={(e) => setContact({ ...contact, relationship: e.target.value })}
                        data-testid="contact-relationship"
                        className="mt-1 w-full rounded-xl border border-navy-200 bg-white/80 px-4 py-3 text-sm focus:outline-none focus:border-gold-500"
                      >
                        <option value="">Select…</option>
                        <option>Executor / Administrator</option>
                        <option>Trustee</option>
                        <option>Heir / Beneficiary</option>
                        <option>Spouse / Partner</option>
                        <option>Child / Grandchild</option>
                        <option>Sibling</option>
                        <option>Other family member</option>
                        <option>Attorney / Advisor</option>
                        <option>Prefer not to say</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-navy-700">Best time to contact you</label>
                      <select
                        value={contact.best_time}
                        onChange={(e) => setContact({ ...contact, best_time: e.target.value })}
                        data-testid="contact-best-time"
                        className="mt-1 w-full rounded-xl border border-navy-200 bg-white/80 px-4 py-3 text-sm focus:outline-none focus:border-gold-500"
                      >
                        <option>Morning (8am–12pm)</option>
                        <option>Afternoon (12pm–5pm)</option>
                        <option>Evening (5pm–8pm)</option>
                        <option>Weekend</option>
                        <option>Anytime</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-navy-700">How can we help?</label>
                    <textarea
                      required
                      rows={5}
                      value={contact.message}
                      onChange={(e) => setContact({ ...contact, message: e.target.value })}
                      data-testid="contact-message"
                      className="mt-1 w-full rounded-xl border border-navy-200 bg-white/80 px-4 py-3 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20"
                      placeholder="Tell us a little about the estate..."
                    />
                  </div>
                  <button type="submit" disabled={busy} data-testid="contact-submit" className="btn-primary disabled:opacity-60 group">
                    {busy ? "Sending..." : "Send Message"}
                    <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                  </button>
                </form>
              ) : (
                <form onSubmit={submitAppt} className="space-y-4" data-testid="appointment-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input label="Name" required value={appt.name} onChange={(v) => setAppt({ ...appt, name: v })} testid="appt-name" />
                    <Input label="Email" type="email" required value={appt.email} onChange={(v) => setAppt({ ...appt, email: v })} testid="appt-email" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input label="Phone" value={appt.phone} onChange={(v) => setAppt({ ...appt, phone: v })} testid="appt-phone" />
                    <Input label="Preferred date" type="date" required value={appt.preferred_date} onChange={(v) => setAppt({ ...appt, preferred_date: v })} testid="appt-date" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-navy-700">Preferred time</label>
                    <select
                      value={appt.preferred_time}
                      onChange={(e) => setAppt({ ...appt, preferred_time: e.target.value })}
                      data-testid="appt-time"
                      className="mt-1 w-full rounded-xl border border-navy-200 bg-white/80 px-4 py-3 text-sm focus:outline-none focus:border-gold-500"
                    >
                      {["9:00 AM","10:00 AM","11:00 AM","1:00 PM","2:00 PM","3:00 PM","4:00 PM"].map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-navy-700">Notes</label>
                    <textarea
                      rows={3}
                      value={appt.notes}
                      onChange={(e) => setAppt({ ...appt, notes: e.target.value })}
                      data-testid="appt-notes"
                      className="mt-1 w-full rounded-xl border border-navy-200 bg-white/80 px-4 py-3 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20"
                      placeholder="Anything we should know in advance?"
                    />
                  </div>
                  <button type="submit" disabled={busy} data-testid="appt-submit" className="btn-gold disabled:opacity-60 group">
                    <Calendar size={16} className="mr-2" />
                    {busy ? "Booking..." : "Request Appointment"}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="h-10 w-10 shrink-0 rounded-xl bg-gold-500/10 text-gold-600 flex items-center justify-center">
        <Icon size={16} />
      </div>
      <div className="text-sm">
        <div className="text-[10px] uppercase tracking-[0.22em] text-navy-500 font-semibold">{label}</div>
        <div className="text-navy-800 mt-0.5 leading-relaxed">{value}</div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, required, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-navy-700">{label}</label>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full rounded-xl border border-navy-200 bg-white/80 px-4 py-3 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20"
      />
    </div>
  );
}
